import { ChevronDown, EllipsisVertical } from 'lucide-react';
import type { ReactNode } from 'react';
import { useMenuPopover } from '../FilterMenu/useMenuPopover.ts';
import styles from './ActionMenu.module.css';

export type MenuAction = {
  readonly key: string;
  readonly label: string;
  readonly icon: ReactNode;
  readonly onSelect: () => void;
  readonly disabled?: boolean;
  readonly tone?: 'danger';
};

export type ActionMenuProps = {
  label: string;
  actions: readonly MenuAction[];
  text?: string;
};

export const ActionMenu = ({ label, actions, text }: ActionMenuProps) => {
  const { open, toggle, close, menuId, root, list, trigger, moveWithKeys, place } = useMenuPopover(0, 'end');
  return (
    <div ref={root} className={styles['root']}>
      <button
        ref={trigger}
        type="button"
        className={styles['trigger']}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        title={label}
        data-labelled={text === undefined ? undefined : true}
        onClick={toggle}
      >
        {text === undefined ? (
          <EllipsisVertical size={20} aria-hidden="true" />
        ) : (
          <>
            {text}
            <ChevronDown size={16} aria-hidden="true" />
          </>
        )}
      </button>
      {open ? (
        <div
          ref={list}
          id={menuId}
          role="menu"
          tabIndex={-1}
          aria-label={label}
          className={styles['menu']}
          style={place}
          onKeyDown={moveWithKeys}
        >
          {actions.map((action) => (
            <button
              key={action.key}
              type="button"
              role="menuitem"
              className={styles['item']}
              data-tone={action.tone}
              aria-disabled={action.disabled === true ? true : undefined}
              onClick={() => {
                if (action.disabled === true) return;
                close();
                action.onSelect();
              }}
            >
              <span className={styles['icon']} aria-hidden="true">
                {action.icon}
              </span>
              {action.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};
